import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { Menu, X } from "lucide-react";
import { API_URL, Button, BarreNavigation } from "./Dashboard";

const couleurs = ["Indigo", "BlueViolet", "DodgerBlue", "DarkTurquoise", "SeaGreen", "LimeGreen", "Gold", "DarkOrange", "Tomato", "Crimson", "HotPink"];
const icones = ["Activity", "Cpu", "FileSearch", "Users", "UserCheck", "Cog", "Figma", "Database", "Heart", "Monitor", "Brain", "Layers", "BarChart2", "ShieldCheck", "Sliders", "Bug"];

const ChoisirCouleur = ({ selected, onSelect }) => (
  <div className="flex flex-wrap gap-2">
    {couleurs.map((c) => (
      <button
        key={c}
        type="button"
        onClick={() => onSelect(c)}
        className={`w-8 h-8 rounded-full border-2 ${selected === c ? "border-black" : "border-transparent"}`}
        style={{ backgroundColor: c }}
      />
    ))}
  </div>
);

const IconPicker = ({ selected, onSelect }) => (
  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
    {icones.map((iconName) => {
      const Icon = LucideIcons[iconName];
      return (
        <button
          key={iconName}
          type="button"
          onClick={() => onSelect(iconName)}
          className={`p-2 border rounded-xl flex items-center justify-center ${selected === iconName ? "border-blue-600 bg-blue-50" : "border-gray-200"}`}
        >
          <Icon size={20} />
        </button>
      );
    })}
  </div>
);

const Etudes = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [etudes, setEtudes] = useState([]);
  const [montrerAjouterEtude, setMontrerAjouterEtude] = useState(false);
  const [montrerSupprimerEtude, setMontrerSupprimerEtude] = useState(false);
  const [montrerModifierEtude, setMontrerModifierEtude] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) navigate("/");
    else setUser(storedUser);

    fetch(`${API_URL}/obtenirToutesLesEtudes.php`)
      .then((res) => res.json())
      .then((data) => setEtudes(data))
      .catch(console.error);
  }, [navigate]);

  if (!user) return null;

  const peutGererEtudes = ["Responsable DevCo", "Chargé d’affaires", "Chargée d’affaires"].includes(user.poste);

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-gray-50 relative overflow-x-hidden">
        
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className={`
        fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-md transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
         <BarreNavigation 
            user={user} 
            deconnexion={handleLogout}
            onNavigate={() => setIsMobileMenuOpen(false)} 
         />
         <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-4 right-4 lg:hidden text-gray-500"
        >
            <X size={24} />
        </button>
      </div>

      <main className="flex-1 p-4 lg:p-8 w-full">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {/* Bouton Menu Burger */}
            <button 
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
            <h1 className="text-2xl font-bold text-gris-sombre">Toutes les études</h1>
          </div>

          {peutGererEtudes ? (
            <div className="flex gap-2">
              <Button onClick={() => setMontrerAjouterEtude(true)} title="Ajouter" className="flex items-center justify-center">
                <LucideIcons.Plus size={16} className="sm:mr-2"/>
                <span className="hidden sm:inline">Ajouter</span>
              </Button>

              <Button variant="outline" onClick={() => setMontrerModifierEtude(true)} title="Modifier" className="flex items-center justify-center">
                <LucideIcons.Pencil size={16} className="sm:mr-2"/>
                <span className="hidden sm:inline">Modifier</span>
              </Button>
              
              <Button variant="outline" onClick={() => setMontrerSupprimerEtude(true)} title="Supprimer" className="text-red-600 border-red-400 hover:bg-red-100 flex items-center justify-center">
                <LucideIcons.Trash2 size={16} className="sm:mr-2"/>
                <span className="hidden sm:inline">Supprimer</span>
              </Button>
            </div>
          ) : (
            <div className="flex items-center space-x-4">
              {/* Bouton DECONNEXION : Le voici corrigé comme sur le Dashboard */}
              <Button variant="outline" onClick={handleLogout} title="Se déconnecter">
                <span className="hidden sm:inline">Se déconnecter</span>
                <LucideIcons.LogOut size={16} className="sm:hidden" />
              </Button>
            </div>
          )}
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {etudes.map((etude, index) => {
            const Icone = LucideIcons[etude.icone] || LucideIcons.FileText;
            const termine = Number(etude.terminee) === 1;

            return (
              <div
                key={index}
                onClick={() => window.open(etude.lien, "_blank")}
                className="bg-white/50 rounded-2xl shadow p-1 cursor-pointer transition transform hover:scale-[1.02] hover:bg-gray-200/70"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 overflow-hidden">
                      <div
                        className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full"
                        style={{ backgroundColor: etude.couleur }}
                      >
                        <Icone size={18} color="white" />
                      </div>
                      <div className="overflow-hidden">
                        <p className="font-semibold text-gray-800 truncate">{etude.nom}</p>
                        <p className="text-sm text-gray-500">{etude.chiffrage} €</p>
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ml-2 ${
                        termine
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {termine ? "Terminée" : "En cours"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </section>
      </main>

      {montrerAjouterEtude && <AjouterEtude onClose={() => setMontrerAjouterEtude(false)}/>}
      {montrerModifierEtude && <ModifierEtude onClose={() => setMontrerModifierEtude(false)} etudes={etudes}/>}
      {montrerSupprimerEtude && <SupprimerEtude onClose={() => setMontrerSupprimerEtude(false)} etudes={etudes}/>}
    </div>
  );
};

const AjouterEtude = ({ onClose }) => {
  const [nom, setNom] = useState("");
  const [chiffrage, setChiffrage] = useState("");
  const [lien, setLien] = useState("");
  const [couleur, setCouleur] = useState("DodgerBlue");
  const [icone, setIcone] = useState("FileSearch");

  const ajouterEtude = (e) => {
    e.preventDefault();
    fetch(`${API_URL}/ajouterEtude.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nom, chiffrage, lien, couleur, icone }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          alert("Étude ajoutée avec succès !");
          onClose();
          window.location.reload();
        } else {
          alert(data.error || "Erreur inconnue");
        }
      })
      .catch(console.error);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg overflow-y-auto max-h-[90vh]">
        <h2 className="text-xl font-semibold mb-4">Ajouter une étude</h2>
        <form onSubmit={ajouterEtude} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nom de l'étude</label>
            <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} className="w-full border rounded-lg px-3 py-2" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Chiffrage (en €, TTC)</label>
            <input type="number" value={chiffrage} onChange={(e) => setChiffrage(e.target.value)} className="w-full border rounded-lg px-3 py-2" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Lien vers le dossier Google Drive</label>
            <input type="url" value={lien} onChange={(e) => setLien(e.target.value)} className="w-full border rounded-lg px-3 py-2" placeholder="https://drive.google.com/..." required/>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Couleur</label>
            <ChoisirCouleur selected={couleur} onSelect={setCouleur} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Icône</label>
            <IconPicker selected={icone} onSelect={setIcone} />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={onClose} type="button">Annuler</Button>
            <Button type="submit">Enregistrer</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SupprimerEtude = ({ onClose, etudes }) => {
  const [selectedNom, setSelectedNom] = useState("");

  const supprimerEtude = (e) => {
    e.preventDefault();
    if (!selectedNom) return alert("Veuillez sélectionner une étude.");

    fetch(`${API_URL}/supprimerEtude.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nom: selectedNom }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          alert("Étude supprimée avec succès !");
          onClose();
          window.location.reload();
        } else {
          alert(data.error || "Erreur inconnue");
        }
      })
      .catch(console.error);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Supprimer une étude</h2>
        <form onSubmit={supprimerEtude} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Sélectionnez l'étude à supprimer</label>
            <select value={selectedNom} onChange={(e) => setSelectedNom(e.target.value)} className="w-full border rounded-lg px-3 py-2" required>
              <option value="">-- Choisir une étude --</option>
              {etudes.map((etude) => (
                <option key={etude.nom} value={etude.nom}>{etude.nom}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={onClose} type="button">Annuler</Button>
            <Button type="submit" className="bg-red-600 hover:bg-red-700">Supprimer</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ModifierEtude = ({ onClose, etudes }) => {
  const [selectedNom, setSelectedNom] = useState("");
  const [nom, setNom] = useState("");
  const [chiffrage, setChiffrage] = useState("");
  const [lien, setLien] = useState("");
  const [couleur, setCouleur] = useState("#1E90FF");
  const [icone, setIcone] = useState("FileText");
  const [terminee, setTerminee] = useState(false);

  const handleSelection = (nomSelectionne) => {
    setSelectedNom(nomSelectionne);
    const etude = etudes.find(e => e.nom === nomSelectionne);
    if (etude) {
      setNom(etude.nom);
      setChiffrage(etude.chiffrage);
      setLien(etude.lien);
      setCouleur(etude.couleur);
      setIcone(etude.icone);
      setTerminee(Number(etude.terminee) === 1);
    }
  };

  const modifierEtude = (e) => {
    e.preventDefault();
    fetch(`${API_URL}/modifierEtude.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ancienNom: selectedNom, nom, chiffrage, lien, couleur, icone, terminee }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          alert("Étude modifiée avec succès !");
          onClose();
          window.location.reload();
        } else {
          alert(data.error || "Erreur inconnue");
        }
      })
      .catch(console.error);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg overflow-y-auto max-h-[90vh]">
        <h2 className="text-xl font-semibold mb-4">Modifier une étude</h2>
        <form onSubmit={modifierEtude} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Sélectionnez l'étude</label>
            <select value={selectedNom} onChange={(e) => handleSelection(e.target.value)} className="w-full border rounded-lg px-3 py-2" required>
              <option value="">-- Choisir une étude --</option>
              {etudes.map((etude) => (
                <option key={etude.nom} value={etude.nom}>{etude.nom}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nom de l'étude</label>
            <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} className="w-full border rounded-lg px-3 py-2" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Chiffrage (€)</label>
            <input type="number" value={chiffrage} onChange={(e) => setChiffrage(e.target.value)} className="w-full border rounded-lg px-3 py-2" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Lien Google Drive</label>
            <input type="url" value={lien} onChange={(e) => setLien(e.target.value)} className="w-full border rounded-lg px-3 py-2" required/>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Couleur</label>
            <ChoisirCouleur selected={couleur} onSelect={setCouleur} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Icône</label>
            <IconPicker selected={icone} onSelect={setIcone} />
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" checked={terminee} onChange={(e) => setTerminee(e.target.checked)} />
            <label className="text-sm font-medium">Terminer cette étude</label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={onClose} type="button">Annuler</Button>
            <Button type="submit">Modifier</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Etudes;